import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * ORIVON product page, main section (design/product.html "main-product", DESIGN_SPEC §10.3). Horizon keeps the
 * product form, variant picker, prices and sticky add to cart; these elements add the design's extras:
 *
 * <orivon-pdp-gallery-component> (snippets/orivon-pdp-gallery.liquid)
 *   Scroll-snap track inside the arch window: thumbnails, dots and arrows follow the scroll position; arrows and
 *   ←/→/Home/End on the focused track move it. Hover zoom follows the mouse (fine pointers only). A photo opens
 *   the full-screen zoom dialog: Enter, +, − or a click zooms 2.2× at the pointer, drag pans, ←/→ change photo.
 *   Choosing a size (Horizon's ProductSelectEvent) slides to that variant's image and updates the plate price.
 *   The zoom button's labels come from data-zoom-in-label / data-zoom-out-label (locales/en.default.json,
 *   orivon.pdp.gallery_zoom_in / gallery_zoom_out); the English strings here are fallbacks only.
 * <orivon-pdp-live-component data-live-id> (blocks/orivon-pdp-badges.liquid, sections/orivon-pdp-band.liquid)
 *   Variant-dependent markup rendered by Liquid: on a size change its contents are replaced with the same
 *   element from the re-rendered product page that Horizon's variant picker already fetched. The band sits in a
 *   section of its own, so its elements listen page-wide (data-live-scope="document", data-product-id).
 * <orivon-pdp-accordion-component> (blocks/orivon-pdp-accordions.liquid)
 *   Disclosure rows: the trigger toggles aria-expanded and .is-open (the panel animates in orivon-base.css).
 * <orivon-pdp-pincode-component> (blocks/orivon-pdp-pincode.liquid)
 *   Checks for a 6-digit Indian pincode and shows the placeholder result copy (no delivery service is connected).
 *
 * Every element re-initialises through connectedCallback when the theme editor re-renders the section.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Runs `handler` with the ProductSelectEvent result (the re-rendered page) for variant changes of the main product
 * in the element's section (or in the quick-add dialog it sits in). Product cards are ignored.
 *
 * With `page`, for sections below the main product (they share its page, not its section): listens on the
 * document and follows only the main product's picker. Events from product cards, dialogs and quick add are
 * ignored, and so are other products (`productId`) and combined-listing switches to another product. The main
 * picker fetches the whole product page (assets/variant-picker.js), so `detail.html` holds those sections too.
 * @param {HTMLElement} host
 * @param {(detail: { html: Document, resource?: any }) => void} handler
 * @param {AbortSignal} signal
 * @param {{ page?: boolean, productId?: string }} [options]
 */
function onProductSelect(host, handler, signal, { page = false, productId = '' } = {}) {
  const scope = page ? document : host.closest('.shopify-section, dialog');
  const ignore = page ? 'product-card, dialog, quick-add-component' : 'product-card';
  scope?.addEventListener(
    StandardEvents.productSelect,
    (/** @type {any} */ event) => {
      if (!(event.target instanceof Element) || event.target.closest(ignore)) return;
      event.promise
        ?.then((/** @type {any} */ result) => {
          const detail = result?.detail;
          if (!detail?.html) return;
          if (page && (detail.newProduct || (productId && String(detail.productId) !== productId))) return;
          handler(detail);
        })
        .catch((/** @type {any} */ error) => {
          if (error?.name !== 'AbortError') console.warn('[orivon-pdp] product select failed:', error);
        });
    },
    { signal }
  );
}

/**
 * @typedef {object} GalleryRefs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement[]} [thumbs]
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLElement} [platePrice]
 * @property {HTMLButtonElement[]} [zoomButtons]
 * @property {HTMLDialogElement} [zoomDialog]
 * @property {HTMLImageElement} [zoomImg]
 * @property {HTMLElement} [zoomViewport]
 * @property {HTMLElement} [zoomCount]
 * @property {HTMLButtonElement} [zoomToggle]
 *
 * @extends {Component<GalleryRefs>}
 */
export class OrivonPdpGalleryComponent extends Component {
  requiredRefs = ['track'];

  #index = 0;
  #zoomIndex = 0;
  #frame = 0;
  /** @type {HTMLElement | null} */
  #hoverSlide = null;
  /** @type {{ x: number, y: number, left: number, top: number, moved: boolean } | null} */
  #drag = null;
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    const { track, zoomViewport, zoomDialog } = this.refs;

    this.#index = clamp(Number(this.dataset.start) || 0, 0, this.#slides.length - 1);
    if (this.#index > 0) requestAnimationFrame(() => this.#scrollTo(this.#index, false));

    track.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    track.addEventListener('pointermove', this.#handleHover, { passive: true, signal });
    track.addEventListener('pointerleave', this.#clearHover, { signal });
    track.addEventListener('pointerdown', this.#clearHover, { signal });

    if (zoomViewport && zoomDialog) {
      zoomViewport.addEventListener('click', this.#handleViewportClick, { signal });
      zoomViewport.addEventListener('keydown', this.#handleViewportKey, { signal });
      zoomViewport.addEventListener('pointerdown', this.#handlePanStart, { signal });
      window.addEventListener('pointermove', this.#handlePanMove, { passive: true, signal });
      window.addEventListener('pointerup', this.#handlePanEnd, { signal });
      zoomDialog.addEventListener('close', () => this.#setZoomed(false), { signal });
    }

    onProductSelect(this, this.#handleVariant, signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = new AbortController();
    cancelAnimationFrame(this.#frame);
  }

  get #slides() {
    return this.refs.slides ?? [];
  }

  /** @param {number} index */
  go(index) {
    this.#scrollTo(clamp(Number(index) || 0, 0, this.#slides.length - 1), true);
  }

  previous() {
    const count = this.#slides.length;
    if (count > 1) this.#scrollTo((this.#index - 1 + count) % count, true);
  }

  next() {
    const count = this.#slides.length;
    if (count > 1) this.#scrollTo((this.#index + 1) % count, true);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.target !== this.refs.track) return;
    const last = this.#slides.length - 1;
    const keys = /** @type {Record<string, number>} */ ({
      ArrowLeft: this.#index - 1,
      ArrowRight: this.#index + 1,
      Home: 0,
      End: last,
    });
    if (!(event.key in keys)) return;
    event.preventDefault();
    this.#scrollTo(clamp(keys[event.key] ?? 0, 0, last), true);
  }

  /** @param {number} index - photo index (photos come first in the track) */
  zoom(index) {
    const { zoomDialog } = this.refs;
    if (!zoomDialog || this.closest('.quick-add-modal')) return;
    this.#clearHover();
    this.#showZoom(Number(index) || 0);
    if (!zoomDialog.open) zoomDialog.showModal();
    this.refs.zoomViewport?.focus({ preventScroll: true });
  }

  /** @param {number} delta */
  zoomStep(delta) {
    this.#showZoom(this.#zoomIndex + (Number(delta) || 0));
  }

  toggleZoom() {
    this.#setZoomed(!this.refs.zoomDialog?.classList.contains('is-zoomed'));
  }

  closeZoom() {
    this.refs.zoomDialog?.close();
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   */
  #scrollTo(index, animate) {
    const { track } = this.refs;
    if (!this.#slides[index]) return;
    this.#clearHover();
    track.scrollTo({
      left: index * track.clientWidth,
      behavior: animate && !reducedMotion.matches ? 'smooth' : 'auto',
    });
    this.#setCurrent(index);
  }

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const { track } = this.refs;
      const width = track.clientWidth || 1;
      const index = clamp(Math.round(track.scrollLeft / width), 0, this.#slides.length - 1);
      if (index !== this.#index) this.#setCurrent(index);
    });
  };

  /** @param {number} index */
  #setCurrent(index) {
    this.#index = index;
    this.#slides.forEach((slide, i) => {
      slide.inert = i !== index;
    });
    for (const list of [this.refs.thumbs ?? [], this.refs.dots ?? []]) {
      list.forEach((button, i) => button.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }
  }

  /** @param {PointerEvent} event */
  #handleHover = (event) => {
    if (event.pointerType !== 'mouse' || !finePointer.matches || event.buttons) return;
    const target = /** @type {Element} */ (event.target);
    const button = target.closest?.('.o-pdp-slide__zoom');
    const slide = button?.closest('.o-pdp-slide');
    const img = button?.querySelector('img');
    if (!button || !(slide instanceof HTMLElement) || !img) {
      this.#clearHover();
      return;
    }
    const rect = button.getBoundingClientRect();
    img.style.setProperty('--zx', `${clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100).toFixed(1)}%`);
    img.style.setProperty('--zy', `${clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100).toFixed(1)}%`);
    if (this.#hoverSlide !== slide) {
      this.#clearHover();
      slide.classList.add('is-zooming');
      this.#hoverSlide = slide;
    }
  };

  #clearHover = () => {
    this.#hoverSlide?.classList.remove('is-zooming');
    this.#hoverSlide = null;
  };

  /** @param {{ html: Document, resource?: any }} detail */
  #handleVariant = ({ html, resource }) => {
    const fresh = html.querySelector('orivon-pdp-gallery-component');
    const price = fresh?.querySelector('[ref="platePrice"]');
    if (price && this.refs.platePrice) this.refs.platePrice.textContent = price.textContent;

    const mediaId = resource?.featured_media?.id;
    let index = mediaId ? this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId)) : -1;
    if (index < 0 && fresh instanceof HTMLElement) index = Number(fresh.dataset.start);
    if (index >= 0 && index !== this.#index) this.#scrollTo(index, true);
  };

  /** @param {number} index */
  #showZoom(index) {
    const buttons = this.refs.zoomButtons ?? [];
    const { zoomImg, zoomCount } = this.refs;
    if (!buttons.length || !zoomImg) return;
    this.#zoomIndex = (index + buttons.length) % buttons.length;
    const source = /** @type {HTMLButtonElement} */ (buttons[this.#zoomIndex]);
    zoomImg.width = Number(source.dataset.zoomWidth) || 1500;
    zoomImg.height = Number(source.dataset.zoomHeight) || 1500;
    zoomImg.alt = source.dataset.zoomAlt || '';
    zoomImg.src = source.dataset.zoomSrc || '';
    if (zoomCount) zoomCount.textContent = `${this.#zoomIndex + 1} / ${buttons.length}`;
    this.#setZoomed(false);
  }

  /**
   * @param {boolean} on
   * @param {number} [fx] - focus point, 0–1 of the image width
   * @param {number} [fy] - focus point, 0–1 of the image height
   */
  #setZoomed(on, fx = 0.5, fy = 0.5) {
    const { zoomDialog, zoomViewport, zoomImg, zoomToggle } = this.refs;
    if (!zoomDialog || !zoomViewport || !zoomImg) return;
    if (on) zoomViewport.style.setProperty('--zoom-h', `${Math.max(240, zoomViewport.clientHeight - 32)}px`);
    zoomDialog.classList.toggle('is-zoomed', on);
    zoomToggle?.setAttribute('aria-pressed', on ? 'true' : 'false');
    zoomToggle?.setAttribute(
      'aria-label',
      on ? this.dataset.zoomOutLabel || 'Zoom out' : this.dataset.zoomInLabel || 'Zoom in'
    );
    if (!on) return;
    requestAnimationFrame(() => {
      zoomViewport.scrollLeft = zoomImg.offsetLeft + zoomImg.offsetWidth * fx - zoomViewport.clientWidth / 2;
      zoomViewport.scrollTop = zoomImg.offsetTop + zoomImg.offsetHeight * fy - zoomViewport.clientHeight / 2;
    });
  }

  /** @param {MouseEvent} event */
  #handleViewportClick = (event) => {
    const { zoomDialog, zoomImg } = this.refs;
    if (!zoomDialog || !zoomImg || this.#drag?.moved) return;
    if (zoomDialog.classList.contains('is-zoomed')) {
      this.#setZoomed(false);
      return;
    }
    const rect = zoomImg.getBoundingClientRect();
    this.#setZoomed(
      true,
      clamp((event.clientX - rect.left) / (rect.width || 1), 0, 1),
      clamp((event.clientY - rect.top) / (rect.height || 1), 0, 1)
    );
  };

  /** @param {KeyboardEvent} event */
  #handleViewportKey = (event) => {
    const zoomed = Boolean(this.refs.zoomDialog?.classList.contains('is-zoomed'));
    if (event.key === 'Enter' || event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.#setZoomed(event.key === 'Enter' ? !zoomed : true);
    } else if (event.key === '-') {
      event.preventDefault();
      this.#setZoomed(false);
    } else if (!zoomed && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      event.preventDefault();
      this.zoomStep(event.key === 'ArrowRight' ? 1 : -1);
    }
  };

  /** @param {PointerEvent} event */
  #handlePanStart = (event) => {
    const { zoomDialog, zoomViewport } = this.refs;
    if (event.pointerType !== 'mouse' || !zoomViewport || !zoomDialog?.classList.contains('is-zoomed')) {
      this.#drag = null;
      return;
    }
    this.#drag = {
      x: event.clientX,
      y: event.clientY,
      left: zoomViewport.scrollLeft,
      top: zoomViewport.scrollTop,
      moved: false,
    };
  };

  /** @param {PointerEvent} event */
  #handlePanMove = (event) => {
    const drag = this.#drag;
    const { zoomViewport } = this.refs;
    if (!drag || !zoomViewport || !(event.buttons & 1)) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
    if (drag.moved) {
      zoomViewport.scrollLeft = drag.left - dx;
      zoomViewport.scrollTop = drag.top - dy;
    }
  };

  #handlePanEnd = () => {
    if (this.#drag) setTimeout(() => (this.#drag = null), 0);
  };
}

/**
 * Replaces its contents with the same element (matched by data-live-id) from the re-rendered product page.
 * Inside the main product section by default; data-live-scope="document" plus data-product-id lets a section
 * further down the page follow the buy box too (sections/orivon-pdp-band.liquid).
 * The element itself stays, so it may carry data-o-reveal; its contents should not (assets/orivon-core.js only
 * reveals the nodes it found on load, so swapped-in reveal nodes would stay hidden).
 * @extends {Component}
 */
export class OrivonPdpLiveComponent extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    onProductSelect(
      this,
      ({ html }) => {
        const id = this.dataset.liveId;
        if (!id) return;
        const fresh = html.querySelector(`orivon-pdp-live-component[data-live-id="${CSS.escape(id)}"]`);
        if (fresh && fresh.innerHTML !== this.innerHTML) this.innerHTML = fresh.innerHTML;
      },
      this.#controller.signal,
      { page: this.dataset.liveScope === 'document', productId: this.dataset.productId }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = new AbortController();
  }
}

/**
 * Disclosure rows. Triggers: `<button aria-expanded aria-controls on:click="/toggle">` inside `.o-accordion__item`.
 * @extends {Component}
 */
export class OrivonPdpAccordionComponent extends Component {
  /** @param {Event} event */
  toggle(event) {
    const trigger = /** @type {HTMLElement} */ (event.target);
    const item = trigger.closest('.o-accordion__item');
    if (!item) return;
    const open = trigger.getAttribute('aria-expanded') !== 'true';
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    item.classList.toggle('is-open', open);
  }
}

/**
 * @typedef {object} PincodeRefs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} result
 * @property {HTMLElement} message
 *
 * @extends {Component<PincodeRefs>}
 */
export class OrivonPdpPincodeComponent extends Component {
  requiredRefs = ['input', 'result', 'message'];

  /** @param {SubmitEvent} event */
  check(event) {
    event.preventDefault();
    const { input, result, message } = this.refs;
    const value = input.value.replace(/\s+/g, '');
    const valid = /^[1-9][0-9]{5}$/.test(value);
    input.setAttribute('aria-invalid', valid ? 'false' : 'true');
    const template = valid ? this.dataset.valid || '' : this.dataset.invalid || '';
    message.textContent = template.replace('[pincode]', value);
    result.hidden = false;
    result.toggleAttribute('data-o-placeholder', valid);
    if (!valid) input.focus();
  }

  /** Digits only while typing; clears an old error. */
  handleInput() {
    const { input } = this.refs;
    const digits = input.value.replace(/\D+/g, '').slice(0, 6);
    if (digits !== input.value) input.value = digits;
    if (input.getAttribute('aria-invalid') === 'true') input.setAttribute('aria-invalid', 'false');
  }
}

if (!customElements.get('orivon-pdp-gallery-component')) {
  customElements.define('orivon-pdp-gallery-component', OrivonPdpGalleryComponent);
}
if (!customElements.get('orivon-pdp-live-component')) {
  customElements.define('orivon-pdp-live-component', OrivonPdpLiveComponent);
}
if (!customElements.get('orivon-pdp-accordion-component')) {
  customElements.define('orivon-pdp-accordion-component', OrivonPdpAccordionComponent);
}
if (!customElements.get('orivon-pdp-pincode-component')) {
  customElements.define('orivon-pdp-pincode-component', OrivonPdpPincodeComponent);
}
